import React, { useState, useEffect, useCallback } from 'react';
import RequestedLineItems from './RequestedLineItems';
import HREmployeesTable from './HREmployeesTable';
import * as api from '../../api';
import { offersConsiderationStatus, isAwaitingCorrection, correctionTarget, considerationOptionsFor, defaultConsiderationFor } from '../../utils';
import { useToast, Modal, Input, Select, SearchableSelect, Btn, RNLLoader, ReturnDestinationNote } from '../UI';
import { fmtDate } from '../../utils';
import { vatCell } from './RequisitionLineItemsTable';
// ─── helpers ───────────────────────────────────────────────────────────────
const fmt = (v) => (v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '—');
const fmtQty = (v) => (v != null && !isNaN(v) ? parseFloat(v).toLocaleString() : '—');
const properCase = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : v);

// Plain native dropdowns for the CC review grid — no popup modal, fixed width
// so a long selected label truncates in place instead of growing the column.
const ccSelectStyle = {
  width: 140, maxWidth: 140, boxSizing: 'border-box', padding: '4px 6px',
  border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11.5, background: '#f8fbff',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const ccSelectDisabledStyle = { ...ccSelectStyle, background: '#e2e8f0', color: '#94a3b8', cursor: 'not-allowed' };

export default function PRDetailsView({ prId, instanceId, onBack, viewContext, onEdit }) {
  const [prDetails, setPrDetails] = useState(null);
  // The instance this requisition is on. Seeded from the prop when a caller
  // knows it, otherwise filled in from the loaded requisition — every action
  // below reads this rather than the prop, so both routes in behave alike.
  const [wfInstanceId, setWfInstanceId] = useState(instanceId || null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const toast = useToast();

  // Approval Modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReviewed, setActionReviewed] = useState(true);
  const [actionComment, setActionComment] = useState('');
  const [nextApproverId, setNextApproverId] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);
  const [nextStepInfo, setNextStepInfo] = useState(null);
  const [outcomeModal, setOutcomeModal] = useState(null);
  // Configurable "why is this parked" reasons, offered when returning. Only
  // the ones flagged as return reasons come back — the lifecycle entries are
  // set by the system, not picked by a reviewer.
  const [considerationStatuses, setConsiderationStatuses] = useState([]);
  const [considerationStatusId, setConsiderationStatusId] = useState('');
  // Where a return will land. Fetched per instance so the options always
  // match what the server will accept.
  const [returnTargets, setReturnTargets] = useState([]);
  const [returnTarget, setReturnTarget] = useState('');
  // The step doing the returning — decides whether a consideration
  // status is on offer at all. See offersConsiderationStatus.
  const [returnFromStep, setReturnFromStep] = useState('');
  // The action type whose dialog is being prepared, so the control button that
  // was pressed can show the wait while the others lock.
  const [preparingAction, setPreparingAction] = useState(null);

  // Line-item review state (CC or Approval) — there's no separate "save" step
  // anymore; whatever's in lineEdits when an action button is clicked gets
  // saved together with that action (see submitAction).
  const [lineEdits, setLineEdits] = useState({});
  const [ccHeaderGL, setCcHeaderGL] = useState('');
  const [ccHeaderError, setCcHeaderError] = useState(false);
  // Per-line blank-figure flags: { [item_id]: { qty: true, rate: true } }.
  // These figures are what the payment voucher loader sums — a line approved
  // without them lands in the DB with approved_amount NULL, so the requisition
  // reaches APPROVED but can never be loaded into a voucher.
  const [lineErrors, setLineErrors] = useState({});

  // Cost Control can now reassign a line's Project / BOQ / Sub-BOQ as part of
  // their review — needs the same project list + cascading BOQ data the
  // requisition forms use.
  const [projects, setProjects] = useState([]);
  const [boqDataByProject, setBoqDataByProject] = useState({});

  // Rejection reason modal per-line
  const [rejectReasonModal, setRejectReasonModal] = useState(null); // { idx, field, currentReason }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      api.clearApiCache();
      // A DRAFT PR has no workflow instance yet — skip the tracking fetch
      // rather than letting it fail the whole Promise.all.
      const [prRes, userRes, projRes] = await Promise.all([
        api.getPR(prId),
        api.getMe(),
        viewContext === 'cc' ? api.listProjects().catch(() => ({ data: [] })) : Promise.resolve(null),
      ]);
      const pr = prRes?.data || prRes;

      // Opened from a row, the caller already knows the instance and passes
      // it. Opened from a URL there is no row, so it is read off the
      // requisition — which carries workflow_instance_id — at the cost of one
      // extra round trip on that path only.
      const wfId = instanceId || pr?.workflow_instance_id || null;
      setWfInstanceId(wfId);
      const trackRes = wfId ? await api.getWorkflowTracking(wfId).catch(() => null) : null;
      if (projRes) {
        setProjects(Array.isArray(projRes) ? projRes : (projRes.data || []));
      }
      const tracking = trackRes ? (trackRes?.data || trackRes) : {
        requester_details: null,
        approval_history: [],
        current_step: null,
        next_step: null,
        return_reason: null,
        request_status: pr?.request_status,
      };
      const user = userRes?.data || userRes;
      setPrDetails(pr);
      if (pr?.cost_control_general_ledger) {
        setCcHeaderGL(pr.cost_control_general_ledger);
      }
      setTrackingData(tracking);
      setCurrentUser(user);

      // Initialise lineEdits from existing PR item data
      if (pr?.items) {
        const edits = {};
        pr.items.forEach((item) => {
          edits[item.item_id] = {
            cost_control_quantity: item.cost_control_quantity ?? '',
            cost_control_unit_rate: item.cost_control_unit_rate ?? '',
            cost_control_amount: item.cost_control_amount ?? '',
            cost_control_general_ledger: item.cost_control_general_ledger ?? '',
            cost_control_approved: item.cost_control_approved ?? false,
            cost_control_rejected: item.cost_control_rejected ?? false,
            cost_control_rejected_reason: item.cost_control_rejected_reason ?? '',
            approved_quantity: item.approved_quantity ?? '',
            approved_unit_rate: item.approved_unit_rate ?? '',
            approved_amount: item.approved_amount ?? '',
            approved: item.approved ?? false,
            on_hold: item.on_hold ?? false,
            rejected: item.rejected ?? false,
            rejected_reason: item.rejected_reason ?? '',
            project_id: item.project_id ?? '',
            boq_item_id: item.boq_item_id ?? '',
            boq_component_id: item.boq_component_id ?? '',
          };
        });
        setLineEdits(edits);

        // Pre-load BOQ options for whatever project(s) the items already sit under,
        // so the dropdowns show real labels immediately instead of just an id.
        if (viewContext === 'cc') {
          const projectIds = Array.from(new Set(pr.items.map((it) => it.project_id).filter(Boolean)));
          projectIds.forEach(async (pid) => {
            try {
              const boqRes = await api.listBOQ(pid);
              const boqItems = Array.isArray(boqRes) ? boqRes : (boqRes?.data || boqRes?.items || []);
              setBoqDataByProject((prev) => ({ ...prev, [pid]: boqItems }));
            } catch (e) { /* non-fatal */ }
          });
        }
      }
    } catch (err) {
      toast.show('Failed to load PR details', 'error');
    } finally {
      setLoading(false);
    }
  }, [prId, instanceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Which queue tab this was opened from is the ONLY thing that grants an editable
  // review view. Guessing from the viewer's role was wrong: a cost_control-role user
  // looking at their own fully-approved request via "My Requisitions" (or any other
  // non-queue entry point) would get the editable CC form back, letting them "edit"
  // a request that's already been through the whole workflow. Everywhere except the
  // CC Queue / Approval Queue tabs is read-only — except 'generic', used by My Inbox
  // and step-specific inbox views (e.g. Department Head Review), which get a plain
  // Approve/Return action bar with no line-item review step to save first.
  const effectiveView = viewContext === 'cc' ? 'cc' : viewContext === 'approval' ? 'approval' : viewContext === 'generic' ? 'generic' : 'readonly';

  // CC/Approval have a per-line review step, saved together with whichever
  // action button is clicked (see submitAction) rather than as a separate step.
  const requiresReview = effectiveView === 'cc' || effectiveView === 'approval';

  // This screen offered Approve, Return and Reject to whoever was holding the
  // request, with no notion of a request that was sent BACK to them. Holding a
  // correction is not an approval seat: the only move is to fix it and send it
  // on, so Return and Reject do not apply and Approve is really a resubmit.
  const awaitingCorrection = isAwaitingCorrection(prDetails);
  const correctionStep = correctionTarget(prDetails);

  // True once at least one line has an explicit decision.
  const hasAnyReview = () => {
    if (effectiveView === 'cc') {
      return (prDetails?.items || []).some((item) => {
        const e = lineEdits[item.item_id] || {};
        return e.cost_control_approved || e.cost_control_rejected;
      });
    }
    if (effectiveView === 'approval') {
      return (prDetails?.items || []).some((item) => {
        const e = lineEdits[item.item_id] || {};
        return e.approved || e.on_hold || e.rejected;
      });
    }
    return true;
  };

  // True only when EVERY line has been given an explicit decision.
  const hasAllReview = () => {
    if (effectiveView === 'cc') {
      return (prDetails?.items || []).every((item) => {
        const e = lineEdits[item.item_id] || {};
        return e.cost_control_approved || e.cost_control_rejected;
      });
    }
    if (effectiveView === 'approval') {
      return (prDetails?.items || []).every((item) => {
        const e = lineEdits[item.item_id] || {};
        return e.approved || e.on_hold || e.rejected;
      });
    }
    return true;
  };

  // The read-only Line Items table shows Cost Control / Final Approval columns —
  // but a request viewed before it's actually reached those steps (e.g. Department
  // Head Review) has nothing in them yet, so skip rendering the columns entirely
  // rather than showing a block of empty dashes.
  const hasCCData = prDetails?.items?.some((item) =>
    item.cost_control_quantity != null || item.cost_control_unit_rate != null || item.cost_control_amount != null ||
    item.cost_control_approved || item.cost_control_rejected
  );
  const hasApprovalData = prDetails?.items?.some((item) =>
    item.approved_quantity != null || item.approved_unit_rate != null || item.approved_amount != null ||
    item.approved || item.on_hold || item.rejected
  );

  // ─── Line-edit helpers ─────────────────────────────────────────────────────
  const updateLineEdit = (itemId, field, value) => {
    setLineEdits((prev) => {
      const updated = { ...prev[itemId], [field]: value };
      // Auto-calculate amount if qty + rate are both numbers
      if ((field === 'cost_control_quantity' || field === 'cost_control_unit_rate')) {
        const q = parseFloat(field === 'cost_control_quantity' ? value : updated.cost_control_quantity);
        const r = parseFloat(field === 'cost_control_unit_rate' ? value : updated.cost_control_unit_rate);
        if (!isNaN(q) && !isNaN(r)) updated.cost_control_amount = (q * r).toFixed(2);
      }
      if (value !== '' && value !== null && value !== undefined) {
        const key = (field === 'approved_quantity' || field === 'cost_control_quantity') ? 'qty'
          : (field === 'approved_unit_rate' || field === 'cost_control_unit_rate') ? 'rate' : null;
        if (key) {
          setLineErrors((prev) => {
            if (!prev[itemId]?.[key]) return prev;
            const next = { ...prev, [itemId]: { ...prev[itemId] } };
            delete next[itemId][key];
            if (!Object.keys(next[itemId]).length) delete next[itemId];
            return next;
          });
        }
      }
      if ((field === 'approved_quantity' || field === 'approved_unit_rate')) {
        const q = parseFloat(field === 'approved_quantity' ? value : updated.approved_quantity);
        const r = parseFloat(field === 'approved_unit_rate' ? value : updated.approved_unit_rate);
        if (!isNaN(q) && !isNaN(r)) updated.approved_amount = (q * r).toFixed(2);
      }
      return { ...prev, [itemId]: updated };
    });
  };

  // A figure copied out of the API carries the database's scale with it —
  // quantity is Numeric(14,3), so the server sends "10.000" where the approver
  // expects to see 10.00.
  //
  // Shown to two decimals, but NOT with a blind toFixed(2): that column really
  // does hold three decimals, and rounding here would turn a genuine quantity
  // of 0.125 into 0.13. Two decimals are used only where they represent the
  // value exactly; anything finer is left alone.
  const cleanFigure = (v) => {
    if (v == null || v === '') return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return v;
    return n === Number(n.toFixed(2)) ? n.toFixed(2) : String(n);
  };

  // The approver may prefer the requester's own figures to Cost Control's: the same copy, from the requested quantity,
  // estimated rate and amount. A starting point — no line is approved by it.
  const copyRequestToApproval = () => {
    const copyable = (prDetails?.items || []).filter(
      (item) => item.quantity != null || item.estimated_unit_rate != null,
    );

    if (!copyable.length) {
      toast.show('This requisition has no figures to copy.', 'info');
      return;
    }

    setLineEdits((prev) => {
      const next = { ...prev };
      copyable.forEach((item) => {
        const qty = item.quantity;
        const rate = item.estimated_unit_rate;
        const amount = item.estimated_amount;
        next[item.item_id] = {
          ...(next[item.item_id] || {}),
          approved_quantity: cleanFigure(qty),
          approved_unit_rate: cleanFigure(rate),
          approved_amount: amount != null
            ? cleanFigure(amount)
            : (qty != null && rate != null ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : ''),
        };
      });
      return next;
    });

    toast.show(
      `Requested figures copied into ${copyable.length} line(s). `
      + 'Adjust any of them before you approve.',
      'success',
    );
  };

  // Bring Cost Control's figures across into the approver's own columns, for
  // every line at once. Cost Control has already priced the request, so on the
  // common path the approver is agreeing with it — retyping the same numbers
  // line by line was the whole cost of that agreement.
  //
  // A starting point, not a decision: nothing is submitted here, each line
  // stays editable, and the per-line Decision marks are deliberately left
  // alone — copying figures is not the same as accepting a line.
  const copyCCToApproval = () => {
    // Counted BEFORE any state is touched. Counting inside the setLineEdits
    // updater would read zero here, because React runs that updater later.
    const copyable = (prDetails?.items || []).filter(
      (item) => item.cost_control_quantity != null || item.cost_control_unit_rate != null,
    );

    if (!copyable.length) {
      toast.show('Cost Control has not entered any figures on this request yet.', 'info');
      return;
    }

    setLineEdits((prev) => {
      const next = { ...prev };
      copyable.forEach((item) => {
        const qty = item.cost_control_quantity;
        const rate = item.cost_control_unit_rate;
        const amount = item.cost_control_amount;
        next[item.item_id] = {
          ...(next[item.item_id] || {}),
          approved_quantity: cleanFigure(qty),
          approved_unit_rate: cleanFigure(rate),
          // Prefer CC's own total; fall back to qty x rate so the column never
          // disagrees with the two figures beside it.
          approved_amount: amount != null
            ? cleanFigure(amount)
            : (qty != null && rate != null ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : ''),
        };
      });
      return next;
    });

    toast.show(
      `Cost Control figures copied into ${copyable.length} line(s). `
      + 'Adjust any of them before you approve.',
      'success',
    );
  };

  // "Same GL for every line" — on by default, since one ledger for the whole
  // requisition is the ordinary case. Untick it when a request spans accounts
  // and each line carries its own code.
  // Off by default — see MyRequisitions.
  const [glAllLines, setGlAllLines] = useState(false);

  // Push the header code onto every line, so the column always shows what
  // will actually be saved rather than the grid and the server disagreeing.
  const applyHeaderGlToLines = (value) => {
    setLineEdits((prev) => {
      const next = { ...prev };
      (prDetails?.items || []).forEach((item) => {
        next[item.item_id] = { ...(next[item.item_id] || {}), cost_control_general_ledger: value };
      });
      return next;
    });
  };

  // The same idea one step earlier: the requester's figures into Cost
  // Control's own columns, every line at once. Cost Control usually agrees
  // with what was asked and adjusts only the odd line.
  //
  // Like its approval-side twin: a starting point, nothing submitted, every
  // line still editable, and the per-line Decision marks untouched.
  const copyRequestToCC = () => {
    // Counted BEFORE state is touched — inside the updater it would read zero,
    // because React runs that updater later.
    const copyable = (prDetails?.items || []).filter(
      (item) => item.quantity != null || item.estimated_unit_rate != null,
    );

    if (!copyable.length) {
      toast.show('This requisition has no figures to copy.', 'info');
      return;
    }

    setLineEdits((prev) => {
      const next = { ...prev };
      copyable.forEach((item) => {
        const qty = item.quantity;
        const rate = item.estimated_unit_rate;
        const amount = item.estimated_amount;
        next[item.item_id] = {
          ...(next[item.item_id] || {}),
          cost_control_quantity: cleanFigure(qty),
          cost_control_unit_rate: cleanFigure(rate),
          // Prefer the line's own total; fall back to qty x rate so the
          // amount never disagrees with the two figures beside it.
          cost_control_amount: amount != null
            ? cleanFigure(amount)
            : (qty != null && rate != null ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : ''),
        };
      });
      return next;
    });

    toast.show(
      `Requested figures copied into ${copyable.length} line(s). `
      + 'Adjust any of them before you approve.',
      'success',
    );
  };

  // Cost Control reassigning a line's Project / BOQ / Sub-BOQ — same cascading
  // pattern as the requisition forms: changing Project clears the BOQ choices
  // below it, and BOQ data is fetched lazily per project.
  const updateItemProjectOrBoq = (itemId, field, value) => {
    setLineEdits((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
        ...(field === 'project_id' ? { boq_item_id: '', boq_component_id: '' } : {}),
        ...(field === 'boq_item_id' ? { boq_component_id: '' } : {}),
      },
    }));
    if (field === 'project_id' && value && !boqDataByProject[value]) {
      api.listBOQ(value)
        .then((res) => {
          const boqItems = Array.isArray(res) ? res : (res?.data || res?.items || []);
          setBoqDataByProject((prev) => ({ ...prev, [value]: boqItems }));
        })
        .catch(() => {});
    }
  };

  const setCCStatus = (itemId, status) => {
    // status: 'approved' | 'rejected' | 'none'
    setLineEdits((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        cost_control_approved: status === 'approved',
        cost_control_rejected: status === 'rejected',
      },
    }));
    if (status === 'rejected') {
      const item = prDetails.items.find((i) => i.item_id === itemId);
      setRejectReasonModal({
        itemId,
        field: 'cost_control_rejected_reason',
        label: 'Rejection Reason (Cost Control)',
        currentReason: lineEdits[itemId]?.cost_control_rejected_reason || '',
      });
    }
  };

  const setApprovalStatus = (itemId, status) => {
    // status: 'approved' | 'on_hold' | 'rejected' | 'none'
    setLineEdits((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        approved: status === 'approved',
        on_hold: status === 'on_hold',
        rejected: status === 'rejected',
      },
    }));
    if (status === 'rejected' || status === 'on_hold') {
      setRejectReasonModal({
        itemId,
        field: 'rejected_reason',
        label: status === 'on_hold' ? 'Reason for Hold (Approval)' : 'Rejection Reason (Approval)',
        currentReason: lineEdits[itemId]?.rejected_reason || '',
      });
    }
  };

  // Builds the CC/Approval item-review payload from whatever's currently in
  // lineEdits. When autoFillStatus is set (no manual review was done), every
  // line is stamped with that status instead, and the modal's general comment
  // becomes each line's rejected/hold reason — there's no per-line reason to
  // fall back on since nothing was actually reviewed.
  const buildReviewItems = (autoFillStatus) => {
    return prDetails.items.map((item) => {
      const e = lineEdits[item.item_id] || {};
      if (effectiveView === 'cc') {
        const hasDecision = e.cost_control_approved || e.cost_control_rejected;
        const fill = !hasDecision && autoFillStatus;
        return {
          item_id: item.item_id,
          cost_control_quantity: e.cost_control_quantity !== '' ? parseFloat(e.cost_control_quantity) : null,
          cost_control_unit_rate: e.cost_control_unit_rate !== '' ? parseFloat(e.cost_control_unit_rate) : null,
          cost_control_amount: e.cost_control_amount !== '' ? parseFloat(e.cost_control_amount) : null,
          cost_control_general_ledger: (e.cost_control_general_ledger ?? '').trim() || null,
          cost_control_approved: fill ? autoFillStatus === 'approved' : (e.cost_control_approved || false),
          cost_control_rejected: fill ? autoFillStatus === 'rejected' : (e.cost_control_rejected || false),
          cost_control_rejected_reason: fill && autoFillStatus === 'rejected' ? actionComment : (e.cost_control_rejected_reason || null),
          project_id: e.project_id || item.project_id,
          boq_item_id: e.boq_item_id || null,
          boq_component_id: e.boq_component_id || null,
        };
      }
      const hasDecision = e.approved || e.on_hold || e.rejected;
      const fill = !hasDecision && autoFillStatus;
      return {
        item_id: item.item_id,
        approved_quantity: e.approved_quantity !== '' ? parseFloat(e.approved_quantity) : null,
        approved_unit_rate: e.approved_unit_rate !== '' ? parseFloat(e.approved_unit_rate) : null,
        approved_amount: e.approved_amount !== '' ? parseFloat(e.approved_amount) : null,
        approved: fill ? autoFillStatus === 'approved' : (e.approved || false),
        on_hold: fill ? autoFillStatus === 'on_hold' : (e.on_hold || false),
        rejected: fill ? autoFillStatus === 'rejected' : (e.rejected || false),
        rejected_reason: fill && (autoFillStatus === 'rejected' || autoFillStatus === 'on_hold')
          ? actionComment
          : (e.rejected_reason || null),
      };
    });
  };

  // ─── Action Modal ──────────────────────────────────────────────────────────
  // Every line that is going to count as payable needs a quantity and a rate.
  // Lines the reviewer has marked rejected or on-hold are exempt — they carry
  // no money forward. Returns true when it's safe to proceed.
  const validateLineFigures = () => {
    if (!requiresReview || !prDetails?.items?.length) return true;
    const qtyField = effectiveView === 'cc' ? 'cost_control_quantity' : 'approved_quantity';
    const rateField = effectiveView === 'cc' ? 'cost_control_unit_rate' : 'approved_unit_rate';

    // Unmarked lines are included on purpose: the action button auto-marks
    // them as approved, so they will carry money and need figures too.
    const errors = {};
    const blank = (v) => v === '' || v === null || v === undefined;
    prDetails.items.forEach((item) => {
      const e = lineEdits[item.item_id] || {};
      const isRejected = effectiveView === 'cc' ? e.cost_control_rejected : e.rejected;
      if (isRejected || e.on_hold) return;
      const rowErr = {};
      if (blank(e[qtyField])) rowErr.qty = true;
      if (blank(e[rateField])) rowErr.rate = true;
      if (Object.keys(rowErr).length) errors[item.item_id] = rowErr;
    });

    if (Object.keys(errors).length) {
      setLineErrors(errors);
      toast.show('Enter a quantity and rate on every line you are approving.', 'error');
      return false;
    }
    setLineErrors({});
    return true;
  };

  const handleActionClick = async (type) => {
    // See MyRequisitions: the header GL is a convenience for filling every
    // line at once, not the code that posts. Each line carries its own.
    if (type === 'approve' && effectiveView === 'cc') {
      setCcHeaderError(false);
    }

    if (type === 'approve' && !validateLineFigures()) return;

    setActionType(type);
    setActionReviewed(!requiresReview || hasAllReview());
    setActionComment('');
    setNextApproverId('');
    setNextStepInfo(null);
    setReturnTargets([]); setReturnTarget('');
    setConsiderationStatusId('');

    // Everything the dialog needs is fetched BEFORE it appears. Opening first
    // and filling in afterwards made the modal render at one height and then
    // grow as the next-step or Return-to picker arrived — the flash. The wait
    // now shows on the control button that was pressed.
    setPreparingAction(type);
    try {
      if (type === 'approve') {
        try {
          const details = await api.getNextStepDetails(wfInstanceId);
          const data = details?.data || details;
          setNextStepInfo(data);
          if (data?.approver_mode === 'specific_user' && data?.eligible_users?.length > 0) {
            setNextApproverId(data.eligible_users[0].user_id || data.eligible_users[0].id);
          }
        } catch (err) {
          console.error('Failed to fetch next step details', err);
        }
      }

      if (type === 'return' && wfInstanceId) {
        // Optional extra: a failure hides the picker and the server falls back
        // to the step's configured behaviour, rather than blocking the return.
        const targets = await api.getReturnTargets(wfInstanceId).catch(() => null);
        setReturnTargets(targets?.targets || []);
        setReturnFromStep(targets?.current_step_name || '');
        setReturnTarget(targets?.default_target || 'requester');
      }
    } finally {
      setPreparingAction(null);
    }

    setShowActionModal(true);
  };

  useEffect(() => {
    let cancelled = false;
    api.listConsiderationStatuses({ activeOnly: true, returnReasonsOnly: true })
      .then((res) => {
        if (cancelled) return;
        setConsiderationStatuses(Array.isArray(res) ? res : (res.consideration_statuses || []));
      })
      // Non-fatal: the reason is optional, so a failure here must not block
      // the reviewer from returning a request.
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  // Clearing this when the destination stops being Cost Control: the picker
  // hides, but without this the previously chosen value would still be sent
  // and would park the request under a heading nobody will look at.
  useEffect(() => {
    if (!offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)) {
      setConsiderationStatusId('');
      return;
    }
    // Picking a destination pre-selects the heading that matches it. Seeded
    // on every change of destination, so switching from "the requester" to
    // the department head moves the heading with it — but it is only a
    // starting point, and the approver can pick any other heading after.
    const label = (returnTargets || []).find((t) => t.value === returnTarget)?.label;
    const suggested = defaultConsiderationFor(label, considerationStatuses);
    if (suggested) setConsiderationStatusId(suggested);
  }, [returnTarget, returnTargets, returnFromStep, considerationStatuses]);

  const submitAction = async () => {
    setSubmittingAction(true);
    try {
      const payload = { action: actionType, comment: actionComment };
      // Only meaningful on a return; the server ignores them otherwise.
      if (actionType === 'return' && considerationStatusId) {
        payload.consideration_status_id = considerationStatusId;
      }
      if (actionType === 'return' && returnTarget) {
        payload.return_target = returnTarget;
      }
      // Required now, not optional: the heading is what files the request in
      // Pending Consideration, and a return without one lands nowhere useful.
      // Checked only when the picker is actually shown, so returns from steps
      // that do not use headings are unaffected.
      if (actionType === 'return'
          && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)
          && !considerationStatusId) {
        toast.show('Choose a consideration status for this return.', 'error');
        setSubmittingAction(false);
        return;
      }
      if (!['approve', 'resume'].includes(actionType) && !actionComment.trim()) {
        const requiredMsg = {
          return: 'Comment is required to return this request.',
          reject: 'Comment is required to reject this request.',
          hold: 'Comment is required to put this request on hold.',
        }[actionType] || 'Comment is required.';
        toast.show(requiredMsg, 'error');
        setSubmittingAction(false);
        return;
      }
      if (actionType === 'approve' && nextStepInfo?.approver_mode === 'specific_user') {
        if (!nextApproverId) {
          toast.show('Please select the specific approver for this step.', 'error');
          setSubmittingAction(false);
          return;
        }
        payload.next_approver_id = nextApproverId;
      }

      // No separate "Save Review" step anymore — whatever's in lineEdits is
      // saved together with the action itself. If nothing was reviewed at
      // all, every line auto-fills to match the action being taken.
      // Skipped for resume — lifting a hold is not a decision on the lines,
      // so there is no review to save alongside it.
      if (requiresReview && actionType !== 'resume') {
        let autoFillStatus = null;
        if (actionType === 'approve') autoFillStatus = 'approved';
        else if (actionType === 'reject') autoFillStatus = 'rejected';
        else if (actionType === 'hold') autoFillStatus = 'on_hold';
        const items = buildReviewItems(autoFillStatus);
        if (effectiveView === 'cc') {
          await api.updatePRItemsCCReview(prId, { 
            items,
            cost_control_general_ledger: ccHeaderGL || null
          });
        } else if (effectiveView === 'approval') {
          await api.updatePRItemsApprovalReview(prId, { items });
        }
      }

      await api.actOnWorkflow(wfInstanceId, payload);
      setShowActionModal(false);
      const OUTCOME_COPY = {
        approve: { title: 'Request Approved Successfully!', message: 'The purchase request has been officially approved and forwarded to the next step in the workflow.' },
        return: { title: 'Request Returned!', message: 'The purchase request has been returned to the requester for correction.' },
        reject: { title: 'Request Rejected!', message: 'The purchase request has been rejected. This is final — it cannot be resubmitted.' },
        hold: { title: 'Request Put On Hold!', message: 'The purchase request has been put on hold. It stays with you and you can act on it again later.' },
        resume: { title: 'Hold Lifted!', message: 'The purchase request is active again and back in your queue for a decision.' },
      };
      setOutcomeModal({
        type: actionType,
        ...(OUTCOME_COPY[actionType] || OUTCOME_COPY.approve),
      });
    } catch (err) {
      setShowActionModal(false);
      setOutcomeModal({
        type: 'error',
        title: 'Action Failed',
        message: err.response?.data?.detail || err.message || `Failed to ${actionType} request`,
      });
    } finally {
      setSubmittingAction(false);
    }
  };

  // ─── Loading / Error ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: '40px 20px', display: 'flex', justifyContent: 'center' }}>
        <RNLLoader title="Loading PR Progress Details..." subtitle="Retrieving workflow progress and approval history" />
      </div>
    );
  }

  if (!prDetails || !trackingData) {
    return (
      <div style={{ padding: 20 }}>
        Failed to load data. <button onClick={onBack}>Go Back</button>
      </div>
    );
  }

  const activeStep = trackingData.current_step;

  // Whether the desk this screen is acting AS is the one actually holding the
  // request. Opening a Pending Consideration row that sits with the department
  // head used to show Cost Control's Approve/Return/Reject — buttons the
  // server refuses, because that step is not theirs. The list already made the
  // distinction; this screen did not.
  const holderStepName = (activeStep?.step_name || '').toLowerCase();
  const viewHoldsRequest = effectiveView === 'cc'
    ? /cost control/.test(holderStepName)
    : effectiveView === 'approval'
    ? /approval/.test(holderStepName)
    : true;

  // Timeline nodes in workflow order. History and the current step used to be
  // rendered as two consecutive blocks, which silently assumed every history
  // entry sits BEFORE the current step. return_to_previous breaks that: the
  // step that returned the request is history, but its step_order is higher
  // than the pending step the request fell back to. Sorting fixes the order.
  const timelineNodes = [
    ...(trackingData.approval_history || []).map((hist, idx) => ({
      kind: 'history',
      key: `hist-${hist.step_id || idx}`,
      order: hist.step_order ?? idx + 1,
      hist,
    })),
    ...(trackingData.current_step
      ? [{
          kind: 'current',
          key: 'current',
          // Fall back to the end of the rail if the API didn't send an order.
          order: trackingData.current_step.step_order ?? Number.MAX_SAFE_INTEGER,
        }]
      : []),
  ].sort((a, b) => a.order - b.order);

  // Edit is offered here whenever the caller wired it up (onEdit) AND this
  // request is actually in an editable state: a step-specific reviewer
  // ('generic' — e.g. Department Head Review) can always correct it before
  // deciding, and the owner can correct it while it's DRAFT or RETURNED.
  // (request_status comes through lowercase from the API.)
  //
  // 'readonly' overrides all of it. A department colleague opening someone
  // else's request via the eye button is a spectator: the DRAFT/RETURNED
  // clauses below would otherwise have handed them an EDIT button on a
  // requisition that isn't theirs and isn't assigned to them.
  const requestStatusUpper = (prDetails.request_status || '').toUpperCase();
  // Parked by an approver. The server refuses every decision until the hold is
  // lifted, so the controls follow the same rule rather than offering actions
  // that would come back as an error.
  const onHold = requestStatusUpper === 'ON_HOLD';
  const canEditFromHere = !!onEdit && effectiveView !== 'readonly' && (
    effectiveView === 'generic' ||
    requestStatusUpper === 'DRAFT' ||
    requestStatusUpper === 'RETURNED'
  );

  // ─── Column Header style ───────────────────────────────────────────────────
  const th = (extra = {}) => ({
    padding: '9px 10px',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: 'nowrap',
    background: '#0b318bff',
    color: '#fff',
    borderBottom: '2px solid #0b318bff',
    ...extra,
  });
  const td = (extra = {}) => ({
    padding: '8px 10px',
    fontSize: 13,
    borderBottom: '1px solid #f1f5f9',
    ...extra,
  });
  const numInput = (val, onChange, disabled, hasError = false) => (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <input
        type="number"
        value={val}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 90,
          padding: '4px 6px',
          border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1',
          borderRadius: 4,
          fontSize: 12,
          background: disabled ? '#e2e8f0' : '#f8fafc',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
      {hasError && (
        <div style={{
          position: 'absolute', top: '-30px', left: 0, background: '#ef4444', color: '#fff',
          padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 'bold',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)', whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none',
        }}>
          This field is required
          <div style={{
            position: 'absolute', bottom: '-4px', left: 16, width: 0, height: 0,
            borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
            borderTop: '4px solid #ef4444',
          }} />
        </div>
      )}
    </div>
  );

  // ─── CC status badge colours ───────────────────────────────────────────────
  const ccBadgeColor = (e) => {
    if (e.cost_control_approved) return '#16a34a';
    if (e.cost_control_rejected) return '#ef4444';
    return '#94a3b8';
  };
  const approvalBadgeColor = (e) => {
    if (e.approved) return '#16a34a';
    if (e.on_hold) return '#eab308';
    if (e.rejected) return '#ef4444';
    return '#94a3b8';
  };
  const badgeBgFor = (color) => ({
    '#16a34a': '#dcfce7',
    '#ef4444': '#fee2e2',
    '#eab308': '#fef9c3',
    '#94a3b8': '#f1f5f9',
  }[color] || '#f1f5f9');

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        .action-btn { transition: all 0.2s ease; }
        .action-btn:disabled { cursor: not-allowed !important; filter: grayscale(0.35); }
        .action-btn:disabled:hover { filter: grayscale(0.35); }
        .action-btn-approve:not(:disabled):hover { background-color: #16a34a !important; color: #fff !important; }
        .action-btn-reject:not(:disabled):hover { background-color: #ef4444 !important; color: #fff !important; }
        .review-toggle-btn { border: 2px solid; border-radius: 6px; padding: 4px 10px; font-size: 11px; font-weight: 700; cursor: pointer; transition: all 0.15s; }
        .review-toggle-btn:hover { opacity: 0.85; }
        .approval-review-table th, .approval-review-table td { border: 1px solid #000 !important; }
        .cc-review-table th, .cc-review-table td { border: 1px solid #000 !important; }
        .readonly-items-table th, .readonly-items-table td { border: 1px solid #000 !important; text-align: center !important; padding: 14px 18px !important; min-width: 120px; }
        .readonly-items-table th { white-space: nowrap; }
        @keyframes hourglass-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .hourglass-pending { animation: hourglass-spin 2s linear infinite; transform-origin: center; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={onBack}
            style={{
              padding: '6px 16px',
              background: '#e0f2fe',
              color: '#1e3a8a',
              border: '2px solid #93c5fd',
              borderRadius: 9999,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <h2 style={{ margin: 0, fontSize: 20 }}>Request Details: {prDetails.pr_number}</h2>
        </div>

        {/* Edit + Approve/Reject — top right. Edit shows for the owner while
            DRAFT/RETURNED, or for whoever is currently reviewing it (e.g.
            Department Head Review). The workflow action buttons are only for
            reviewers acting from the CC/Approval queue tabs or a generic
            step review — the requester viewing their own request via "My
            Requisitions" shouldn't see those. */}
        <div style={{ display: 'flex', gap: 10 }}>
          {canEditFromHere && (
            <button
              className="action-btn action-btn-edit"
              onClick={() => onEdit?.(prId)}
              title="Edit this request"
              style={{
                padding: '8px 20px',
                background: '#fff',
                color: '#a16207',
                border: '2px solid #eab308',
                borderRadius: 9999,
                fontWeight: 700,
                fontSize: 13,
                cursor: preparingAction ? 'wait' : 'pointer',
                opacity: preparingAction ? 0.55 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              EDIT REQUEST
            </button>
          )}
          {activeStep && effectiveView !== 'readonly' && viewHoldsRequest && (
            <>
            {(effectiveView === 'approval' || effectiveView === 'cc') && (
              <button
                className="action-btn action-btn-reject"
                onClick={() => handleActionClick('reject')}
                disabled={!!preparingAction || onHold}
                title="Reject this request permanently"
                style={{
                  padding: '8px 20px',
                  background: '#ef4444',
                  color: '#fff',
                  border: '2px solid #ef4444',
                  borderRadius: 9999,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: preparingAction ? 'wait' : (onHold ? 'not-allowed' : 'pointer'),
                  opacity: (preparingAction || onHold) ? 0.55 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                REJECT REQUEST
              </button>
            )}
            {/* Hold and Resume share one slot: a request is either parked or
                it is not, so offering both would leave one a no-op. */}
            {effectiveView === 'approval' && onHold && (
              <button
                className="action-btn action-btn-hold"
                onClick={() => handleActionClick('resume')}
                disabled={!!preparingAction}
                title="Lift the hold and put this request back in the queue"
                style={{
                  padding: '8px 20px',
                  background: '#16a34a',
                  color: '#fff',
                  border: '2px solid #16a34a',
                  borderRadius: 9999,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: preparingAction ? 'wait' : 'pointer',
                  opacity: preparingAction ? 0.55 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                RESUME REQUEST
              </button>
            )}
            {effectiveView === 'approval' && !onHold && (
              <button
                className="action-btn action-btn-hold"
                onClick={() => handleActionClick('hold')}
                disabled={!!preparingAction}
                title="Put this request on hold"
                style={{
                  padding: '8px 20px',
                  background: '#eab308',
                  color: '#fff',
                  border: '2px solid #eab308',
                  borderRadius: 9999,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: preparingAction ? 'wait' : 'pointer',
                  opacity: preparingAction ? 0.55 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                </svg>
                HOLD REQUEST
              </button>
            )}
            {(
            <button
              className="action-btn action-btn-return"
              onClick={() => handleActionClick('return')}
              disabled={!!preparingAction || onHold}
              title="Return this request to the requester"
              style={{
                padding: '8px 20px',
                background: '#fff',
                color: '#f97316',
                border: '2px solid #f97316',
                borderRadius: 9999,
                fontWeight: 700,
                fontSize: 13,
                cursor: preparingAction ? 'wait' : (onHold ? 'not-allowed' : 'pointer'),
                opacity: (preparingAction || onHold) ? 0.55 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" />
              </svg>
              RETURN REQUEST
            </button>
            )}
            <button
              className="action-btn action-btn-approve"
              onClick={() => handleActionClick('approve')}
              disabled={!!preparingAction || onHold}
              title={awaitingCorrection
                ? `Send the corrected request back to ${correctionStep}`
                : 'Approve this request'}
              style={{
                padding: '8px 20px',
                background: '#16a34a',
                color: '#fff',
                border: '2px solid #16a34a',
                borderRadius: 9999,
                fontWeight: 700,
                fontSize: 13,
                cursor: preparingAction ? 'wait' : (onHold ? 'not-allowed' : 'pointer'),
                opacity: (preparingAction || onHold) ? 0.55 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              {awaitingCorrection ? `RESUBMIT TO ${correctionStep.toUpperCase()}` : 'APPROVE REQUEST'}
            </button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Summary */}
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid black' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Request Summary</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, fontSize: 14 }}>
                  <div><strong>Title:</strong> {prDetails.title}</div>
                  <div><strong>Required Date:</strong> {prDetails.required_date || '—'}</div>
                  <div><strong>Status:</strong> {properCase((prDetails.request_status || '').replace(/_/g, ' '))}</div>
                  <div><strong>Priority:</strong> {properCase(prDetails.priority_status)}</div>
                  <div><strong>Total Estimated:</strong> ₦{fmt(prDetails.total_estimated_cost)}</div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', width: 280, marginLeft: 20, paddingLeft: 20, borderLeft: '1px solid black' }}>
                <strong style={{ fontSize: 18, color: '#334155', alignSelf: 'center' }}>Cost Control GL</strong>
                <input
                  type="text"
                  value={effectiveView === 'cc' ? ccHeaderGL : (prDetails.cost_control_general_ledger || '')}
                  onChange={(e) => {
                    if (effectiveView !== 'cc') return;
                    setCcHeaderGL(e.target.value);
                    if (glAllLines) applyHeaderGlToLines(e.target.value);
                    if (e.target.value.trim() && ccHeaderError) {
                      setCcHeaderError(false);
                    }
                  }}
                  disabled={effectiveView !== 'cc'}
                  placeholder={effectiveView === 'cc' ? "Enter GL code" : "—"}
                  style={{
                    padding: '12px 16px',
                    fontSize: 16,
                    border: ccHeaderError ? '2px solid #ef4444' : '1px solid #cbd5e1',
                    borderRadius: 6,
                    outline: 'none',
                    background: effectiveView === 'cc' ? '#fff' : '#f8fafc',
                    width: '100%',
                    fontWeight: 600,
                    color: '#0f172a'
                  }}
                />
                {effectiveView === 'cc' && (
                  <label
                    title="On: the code above fills the CC GL column for every line. Off: type a code on each line."
                    style={{
                      display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                      fontSize: 12, fontWeight: 700, color: '#475569',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={glAllLines}
                      onChange={(ev) => {
                        setGlAllLines(ev.target.checked);
                        // Turning it ON re-applies the header code. Turning it
                        // OFF leaves the lines as they are — they simply become
                        // editable, and blanking them would throw away codes
                        // the reviewer is about to adjust.
                        if (ev.target.checked) applyHeaderGlToLines(ccHeaderGL);
                      }}
                      style={{ cursor: 'pointer', width: 15, height: 15 }}
                    />
                    Same GL for every line
                  </label>
                )}
                {ccHeaderError && (
                  <div style={{
                    position: 'absolute',
                    top: '-32px',
                    right: 0,
                    background: '#ef4444',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                    pointerEvents: 'none'
                  }}>
                    This field is required
                    <div style={{
                      position: 'absolute',
                      bottom: '-4px',
                      right: '16px',
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '4px solid #ef4444'
                    }}></div>
                  </div>
                )}
              </div>
            </div>
            {requestStatusUpper === 'RETURNED' && trackingData.return_reason && (
              <div style={{ marginTop: 16, padding: '10px 14px', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, fontSize: 13, color: '#9a3412' }}>
                <strong>Return Reason:</strong> "{trackingData.return_reason}"
              </div>
            )}
          </div>

          {(() => {
            const pr = prDetails;
            const totalRequested = Number(pr.total_estimated_cost || 0);
            const ccApproved = (pr.items || []).reduce((sum, it) => sum + Number(it.cost_control_amount ?? it.estimated_amount ?? 0), 0);
            const fieldLabel = { fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 };
            const fieldValue = { fontWeight: 700, color: '#0f172a', fontSize: 12.5, marginTop: 2 };

            return (
              <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '12px 16px', marginBottom: 12, marginTop: 12 }}>
                <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
                  Section 1 — Requisition Header
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', rowGap: 14, columnGap: 24 }}>
                  <div>
                    <div style={fieldLabel}>Ref ID</div>
                    <div style={fieldValue}>{pr.pr_number || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Date</div>
                    <div style={fieldValue}>{(pr.required_date || pr.created_at) ? fmtDate(pr.required_date || pr.created_at) : '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Requester</div>
                    <div style={fieldValue}>{pr.requested_by_name || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Employee ID</div>
                    <div style={fieldValue}>{pr.employee_id || '—'}</div>
                  </div>
                  {/* Who the requisition is FOR, when raised on someone's
                      behalf. Shown even when empty so a reviewer can tell
                      "nobody else" from "nobody filled it in". */}
                  <div>
                    <div style={fieldLabel}>Requested For</div>
                    <div style={fieldValue}>{pr.requested_for || '—'}</div>
                  </div>

                  <div>
                    <div style={fieldLabel}>Designation</div>
                    <div style={fieldValue}>{pr.designation || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Department</div>
                    <div style={fieldValue}>{pr.department_name || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Request Type</div>
                    <div style={fieldValue}>{pr.request_type_name || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Project / Site</div>
                    <div style={fieldValue}>{pr.project_name || '—'}</div>
                  </div>

                  <div>
                    <div style={fieldLabel}>Priority</div>
                    <div style={fieldValue}>{pr.priority_status || 'Normal'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Due Date</div>
                    <div style={fieldValue}>{pr.expected_due_date ? fmtDate(pr.expected_due_date) : '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Purpose</div>
                    <div style={fieldValue}>{pr.title || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Remarks</div>
                    <div style={fieldValue}>{pr.description || pr.remarks || '—'}</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Cost Control Review Summary ── */}
          {prDetails.cc_reviewed_by && (() => {
            const rejectedLines = prDetails.cc_rejected_line_count != null
              ? prDetails.cc_rejected_line_count
              : (prDetails.items || []).filter((it) => it.cost_control_rejected).length;
            const approvedLines = (prDetails.items || []).filter((it) => it.cost_control_approved).length;
            const totalRequested = Number(prDetails.total_estimated_cost || 0);
            const ccApproved = (prDetails.items || []).reduce((sum, it) => sum + Number(it.cost_control_amount ?? it.estimated_amount ?? 0), 0);
            const variance = totalRequested - ccApproved;
            const withinBudget = variance >= 0;

            const fieldLabel = { fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 };
            const fieldValue = { fontWeight: 700, color: '#0f172a', fontSize: 12.5, marginTop: 2 };

            return (
              <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontWeight: 800, color: '#2563eb', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
                  Section 2 — Cost Control Review Summary
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', rowGap: 14, columnGap: 24 }}>
                  <div>
                    <div style={fieldLabel}>CC Officer</div>
                    <div style={fieldValue}>{prDetails.cc_reviewed_by || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>CC Review Date</div>
                    <div style={fieldValue}>{prDetails.cc_reviewed_at ? new Date(prDetails.cc_reviewed_at).toLocaleDateString('en-GB') : '—'}</div>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <div style={fieldLabel}>CC Comment</div>
                    <div style={{ ...fieldValue, fontWeight: 600, fontStyle: prDetails.cc_review_comment ? 'italic' : 'normal', lineHeight: 1.5 }}>
                      {prDetails.cc_review_comment ? `"${prDetails.cc_review_comment}"` : '—'}
                    </div>
                  </div>

                  <div>
                    <div style={fieldLabel}>Approved Lines</div>
                    <div style={fieldValue}>{approvedLines}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Rejected Lines</div>
                    <div style={{ ...fieldValue, color: rejectedLines > 0 ? '#ef4444' : '#0f172a' }}>{rejectedLines}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Total Requested</div>
                    <div style={fieldValue}>₦{fmt(totalRequested)}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>CC Approved</div>
                    <div style={{ ...fieldValue, color: '#2563eb' }}>₦{fmt(ccApproved)}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: withinBudget ? '#16a34a' : '#ef4444' }} />
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: withinBudget ? '#16a34a' : '#ef4444' }}>
                      {withinBudget ? 'Within Budget' : 'Over Budget'}
                    </span>
                  </div>
                  <div>
                    <span style={{ ...fieldLabel, marginRight: 6 }}>Variance</span>
                    <span style={{ fontWeight: 800, fontSize: 12.5, color: withinBudget ? '#16a34a' : '#ef4444' }}>₦{fmt(Math.abs(variance))}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* What the requester filled in, read-only and in full — the same
              table the queues show above their working grid. Rendered for
              every view here, because "what was asked for" is the one thing
              every reader of this page needs regardless of their role. */}
          <RequestedLineItems
            items={prDetails.items || []}
            isLogistics={(prDetails.department_name || '').toLowerCase().includes('logistics')}
          />

          {/* Who the line items are for. Renders itself away when no line names
              anybody, so it appears on HR requisitions and stays out of the way
              on every other kind. */}
          <HREmployeesTable items={prDetails.items || []} />

          {/* ── Line Items Table ── */}
          {effectiveView === 'cc' ? (
            // ── COST CONTROL VIEW ──────────────────────────────────────────
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #0b318b' }}>
              <div style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>Line Items — Cost Control Review</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
                  Enter your acceptable Quantity, Rate &amp; Amount, reassign Project/BOQ if needed, then mark each line as Accepted or Rejected.
                  Your review is saved automatically when you Approve, Reject, or Return the request below — there's no separate save step.
                </p>
              </div>

              {/* Sits above the grid and hard right, over the three Cost
                  Control columns it fills — same placement as the approval
                  view's copy button. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>Cost Control values:</span>
                <button
                  type="button"
                  disabled={['returned', 'resubmitted'].includes(prDetails?.queue_arrival)}
                  onClick={copyRequestToCC}
                  title={!['returned', 'resubmitted'].includes(prDetails?.queue_arrival) ? "Copy the requester's quantity, rate and amount into the Cost Control columns for every line. You can still change any line afterwards." : "This request came back to Cost Control after a return: keep the earlier review figures and adjust them line by line."}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', background: '#fff', color: '#1e3a8a',
                    border: '1.5px solid #1e3a8a', borderRadius: 8,
                    fontSize: 12, fontWeight: 800, cursor: !['returned', 'resubmitted'].includes(prDetails?.queue_arrival) ? 'pointer' : 'not-allowed', opacity: !['returned', 'resubmitted'].includes(prDetails?.queue_arrival) ? 1 : 0.45,
                    fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                  onMouseOver={(ev) => { if (['returned', 'resubmitted'].includes(prDetails?.queue_arrival)) return; ev.currentTarget.style.background = '#1e3a8a'; ev.currentTarget.style.color = '#fff'; }}
                  onMouseOut={(ev) => { ev.currentTarget.style.background = '#fff'; ev.currentTarget.style.color = '#1e3a8a'; }}
                >
                  ⧉ Copy requester values
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="cc-review-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, border: '1px solid #000' }}>
                  <thead>
                    <tr>
                      <th style={th()}>Description</th>
                      <th style={th({ textAlign: 'center' })}>Unit</th>
                      <th style={th({ textAlign: 'center', background: '#f0fdf4', color: '#0f172a' })}>Project</th>
                      <th style={th({ textAlign: 'center', background: '#f0fdf4', color: '#0f172a' })}>BOQ Item</th>
                      <th style={th({ textAlign: 'center', background: '#f0fdf4', color: '#0f172a' })}>BOQ Sub-Component</th>
                      <th style={th({ textAlign: 'center' })}>Req. Qty</th>
                      <th style={th({ textAlign: 'center' })}>Req. Rate (₦)</th>
                      <th style={th({ textAlign: 'center' })}>Req. Amt (₦)</th>
                      <th style={th({ textAlign: 'center' })}>VAT (₦)</th>
                      <th style={th({ textAlign: 'center', background: '#eff6ff', color: '#0f172a' })}>CC Qty</th>
                      <th style={th({ textAlign: 'center', background: '#eff6ff', color: '#0f172a' })}>CC Rate (₦)</th>
                      <th style={th({ textAlign: 'center', background: '#eff6ff', color: '#0f172a' })}>CC Amt (₦)</th>
                      <th style={th({ textAlign: 'center', background: '#eff6ff', color: '#0f172a' })}>CC GL</th>
                      <th style={th({ textAlign: 'center', background: '#eff6ff', color: '#0f172a' })}>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prDetails.items?.map((item) => {
                      const e = lineEdits[item.item_id] || {};
                      const rowBoqs = boqDataByProject[e.project_id] || [];
                      const selectedBoq = rowBoqs.find((b) => String(b.boq_item_id || b.id) === String(e.boq_item_id));
                      const subBoqOptions = selectedBoq?.components || [];
                      return (
                        <tr key={item.item_id}>
                          <td style={td()}>{item.description}</td>
                          <td style={td({ textAlign: 'center' })}>{item.unit_of_measure}</td>
                          <td style={td({ background: '#f7fef7', textAlign: 'center' })}>
                            <SearchableSelect
                              value={e.project_id || ''}
                              onChange={(v) => updateItemProjectOrBoq(item.item_id, 'project_id', v)}
                              style={ccSelectStyle}
                              options={projects.map((p) => ({ value: p.id || p.project_id, label: p.project_name || p.name }))}
                            />
                          </td>
                          <td style={td({ background: '#f7fef7', textAlign: 'center' })}>
                            <SearchableSelect
                              value={e.boq_item_id || ''}
                              onChange={(v) => updateItemProjectOrBoq(item.item_id, 'boq_item_id', v)}
                              disabled={!e.project_id}
                              style={!e.project_id ? ccSelectDisabledStyle : ccSelectStyle}
                              options={rowBoqs.map((b) => ({ value: b.boq_item_id || b.id, label: `${b.boq_code || ''} — ${b.description}` }))}
                              placeholder="Select BOQ"
                            />
                          </td>
                          <td style={td({ background: '#f7fef7', textAlign: 'center' })}>
                            <SearchableSelect
                              value={e.boq_component_id || ''}
                              onChange={(v) => updateItemProjectOrBoq(item.item_id, 'boq_component_id', v)}
                              disabled={!e.boq_item_id || subBoqOptions.length === 0}
                              style={(!e.boq_item_id || subBoqOptions.length === 0) ? ccSelectDisabledStyle : ccSelectStyle}
                              options={subBoqOptions.map((c) => ({ value: c.component_id || c.id, label: c.description || c.name }))}
                              placeholder="Select Sub-BOQ"
                            />
                          </td>
                          <td style={td({ textAlign: 'center' })}>{fmtQty(item.quantity)}</td>
                          <td style={td({ textAlign: 'center' })}>₦{fmt(item.estimated_unit_rate)}</td>
                          <td style={td({ textAlign: 'center' })}>₦{fmt(item.estimated_amount)}</td>
                          <td style={td({ textAlign: 'center' })}>{vatCell(item)}</td>
                          <td style={td({ background: '#f8fbff', textAlign: 'center' })}>
                            {numInput(e.cost_control_quantity, (v) => updateLineEdit(item.item_id, 'cost_control_quantity', v), false, !!lineErrors[item.item_id]?.qty)}
                          </td>
                          <td style={td({ background: '#f8fbff', textAlign: 'center' })}>
                            {numInput(e.cost_control_unit_rate, (v) => updateLineEdit(item.item_id, 'cost_control_unit_rate', v), false, !!lineErrors[item.item_id]?.rate)}
                          </td>
                          <td style={td({ background: '#f8fbff', textAlign: 'center' })}>
                            <span style={{ fontWeight: 600, color: '#1e40af' }}>
                              {e.cost_control_amount !== '' ? `₦${fmt(e.cost_control_amount)}` : '—'}
                            </span>
                          </td>
                          {/* Disabled rather than hidden while the switch is
                              on: the reviewer still sees what each line posts
                              to, and the box is already where they expect it
                              the moment they turn the switch off. */}
                          <td style={td({ background: '#f8fbff', textAlign: 'center' })}>
                            <input
                              type="text"
                              value={e.cost_control_general_ledger ?? ''}
                              onChange={(ev) => updateLineEdit(item.item_id, 'cost_control_general_ledger', ev.target.value)}
                              disabled={glAllLines}
                              placeholder={glAllLines ? 'From header' : 'GL code'}
                              style={{
                                width: 100, padding: '5px 7px', fontSize: 12, textAlign: 'center',
                                border: '1px solid #cbd5e1', borderRadius: 5, outline: 'none',
                                background: glAllLines ? '#f1f5f9' : '#fff',
                                color: glAllLines ? '#64748b' : 'inherit',
                                cursor: glAllLines ? 'not-allowed' : 'text',
                              }}
                            />
                          </td>
                          <td style={td({ background: '#f8fbff', textAlign: 'center' })}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                              <button
                                className="review-toggle-btn"
                                onClick={() => setCCStatus(item.item_id, e.cost_control_approved ? 'none' : 'approved')}
                                style={{
                                  borderColor: '#16a34a',
                                  color: e.cost_control_approved ? '#fff' : '#16a34a',
                                  background: e.cost_control_approved ? '#16a34a' : 'transparent',
                                }}
                              >✓ Accept</button>
                              <button
                                className="review-toggle-btn"
                                onClick={() => setCCStatus(item.item_id, e.cost_control_rejected ? 'none' : 'rejected')}
                                style={{
                                  borderColor: '#ef4444',
                                  color: e.cost_control_rejected ? '#fff' : '#ef4444',
                                  background: e.cost_control_rejected ? '#ef4444' : 'transparent',
                                }}
                              >✕ Reject</button>
                            </div>
                            {e.cost_control_rejected && e.cost_control_rejected_reason && (
                              <div style={{ marginTop: 4, fontSize: 10, color: '#ef4444', fontStyle: 'italic', maxWidth: 120 }}>
                                "{e.cost_control_rejected_reason}"
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : effectiveView === 'approval' ? (
            // ── MD / DMD FINAL APPROVAL VIEW ──────────────────────────────
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #0b318b' }}>
              <div style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>Line Items — Final Approval Review</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
                  Review Requester &amp; CC values, enter your approved values, then mark each line.
                  Your review is saved automatically when you Approve, Reject, Hold, or Return the request below — there's no separate save step.
                </p>
              </div>

              {/* Sits above the grid and hard right, over the three
                  "Approved Values" columns it fills. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>Approved values:</span>
                <button
                  type="button"
                  disabled={prDetails?.queue_arrival === 'resubmitted'}
                  onClick={copyCCToApproval}
                  title={prDetails?.queue_arrival !== 'resubmitted' ? "Copy Cost Control's quantity, rate and amount into the approved columns for every line. You can still change any line afterwards." : "The requester corrected this request after it was returned, so the Cost Control figures are from before the correction."}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', background: '#fff', color: '#1e3a8a',
                    border: '1.5px solid #1e3a8a', borderRadius: 8,
                    fontSize: 12, fontWeight: 800, cursor: prDetails?.queue_arrival !== 'resubmitted' ? 'pointer' : 'not-allowed', opacity: prDetails?.queue_arrival !== 'resubmitted' ? 1 : 0.45,
                    fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                  onMouseOver={(ev) => { if (prDetails?.queue_arrival === 'resubmitted') return; ev.currentTarget.style.background = '#1e3a8a'; ev.currentTarget.style.color = '#fff'; }}
                  onMouseOut={(ev) => { ev.currentTarget.style.background = '#fff'; ev.currentTarget.style.color = '#1e3a8a'; }}
                >
                  ⧉ Copy CC values
                </button>
                <button
                  type="button"
                  disabled={prDetails?.queue_arrival !== 'resubmitted'}
                  onClick={copyRequestToApproval}
                  title={prDetails?.queue_arrival === 'resubmitted' ? "Copy the requested quantity, estimated rate and amount into the approved columns for every line. You can still change any line afterwards." : "Available when the requester has corrected and resubmitted a request the Approval Queue returned."}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', background: '#fff', color: '#1e3a8a',
                    border: '1.5px solid #1e3a8a', borderRadius: 8,
                    fontSize: 12, fontWeight: 800, cursor: prDetails?.queue_arrival === 'resubmitted' ? 'pointer' : 'not-allowed', opacity: prDetails?.queue_arrival === 'resubmitted' ? 1 : 0.45,
                    fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                  onMouseOver={(ev) => { if (prDetails?.queue_arrival !== 'resubmitted') return; ev.currentTarget.style.background = '#1e3a8a'; ev.currentTarget.style.color = '#fff'; }}
                  onMouseOut={(ev) => { ev.currentTarget.style.background = '#fff'; ev.currentTarget.style.color = '#1e3a8a'; }}
                >
                  ⧉ Copy requester values
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="approval-review-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={th()} rowSpan={2}>Description</th>
                      <th style={th({ textAlign: 'center' })} rowSpan={2}>Unit</th>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a', textAlign: 'center' })} colSpan={4}>Requester</th>
                      <th style={th({ background: '#eff6ff', color: '#0f172a', textAlign: 'center' })} colSpan={3}>Cost Control</th>
                      <th style={th({ background: '#fefce8', color: '#0f172a', textAlign: 'center' })} colSpan={3}>Approved Values</th>
                      <th style={th({ textAlign: 'center' })} rowSpan={2}>Decision</th>
                    </tr>
                    <tr>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a', textAlign: 'center' })}>Qty</th>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a', textAlign: 'center' })}>Rate (₦)</th>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a', textAlign: 'center' })}>Amt (₦)</th>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a', textAlign: 'center' })}>VAT (₦)</th>
                      <th style={th({ background: '#eff6ff', color: '#0f172a', textAlign: 'center' })}>Qty</th>
                      <th style={th({ background: '#eff6ff', color: '#0f172a', textAlign: 'center' })}>Rate (₦)</th>
                      <th style={th({ background: '#eff6ff', color: '#0f172a', textAlign: 'center' })}>Amt (₦)</th>
                      <th style={th({ background: '#fefce8', color: '#0f172a', textAlign: 'center' })}>Qty</th>
                      <th style={th({ background: '#fefce8', color: '#0f172a', textAlign: 'center' })}>Rate (₦)</th>
                      <th style={th({ background: '#fefce8', color: '#0f172a', textAlign: 'center' })}>Amt (₦)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prDetails.items?.map((item) => {
                      const e = lineEdits[item.item_id] || {};
                      return (
                        <tr key={item.item_id}>
                          <td style={td()}>{item.description}</td>
                          <td style={td({ textAlign: 'center' })}>{item.unit_of_measure}</td>
                          {/* Requester */}
                          <td style={td({ background: '#f7fef7', textAlign: 'center' })}>{fmtQty(item.quantity)}</td>
                          <td style={td({ background: '#f7fef7', textAlign: 'center' })}>₦{fmt(item.estimated_unit_rate)}</td>
                          <td style={td({ background: '#f7fef7', textAlign: 'center' })}>₦{fmt(item.estimated_amount)}</td>
                          <td style={td({ background: '#f7fef7', textAlign: 'center' })}>{vatCell(item)}</td>
                          {/* Cost Control */}
                          <td style={td({ background: '#f0f7ff', textAlign: 'center' })}>{fmtQty(item.cost_control_quantity)}</td>
                          <td style={td({ background: '#f0f7ff', textAlign: 'center' })}>₦{fmt(item.cost_control_unit_rate)}</td>
                          <td style={td({ background: '#f0f7ff', textAlign: 'center' })}>
                            <span style={{ fontWeight: 600, color: ccBadgeColor(item) }}>
                              ₦{fmt(item.cost_control_amount)}
                            </span>
                          </td>
                          {/* Approved Values — editable */}
                          <td style={td({ background: '#fffdf0', textAlign: 'center' })}>
                            {numInput(e.approved_quantity, (v) => updateLineEdit(item.item_id, 'approved_quantity', v), false, !!lineErrors[item.item_id]?.qty)}
                          </td>
                          <td style={td({ background: '#fffdf0', textAlign: 'center' })}>
                            {numInput(e.approved_unit_rate, (v) => updateLineEdit(item.item_id, 'approved_unit_rate', v), false, !!lineErrors[item.item_id]?.rate)}
                          </td>
                          <td style={td({ background: '#fffdf0', textAlign: 'center' })}>
                            <span style={{ fontWeight: 600, color: '#854d0e' }}>
                              {e.approved_amount !== '' ? `₦${fmt(e.approved_amount)}` : '—'}
                            </span>
                          </td>
                          {/* Decision */}
                          <td style={td({ textAlign: 'center' })}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button
                                className="review-toggle-btn"
                                onClick={() => setApprovalStatus(item.item_id, e.approved ? 'none' : 'approved')}
                                style={{
                                  borderColor: '#16a34a',
                                  color: e.approved ? '#fff' : '#16a34a',
                                  background: e.approved ? '#16a34a' : 'transparent',
                                }}
                              >✓</button>
                              <button
                                className="review-toggle-btn"
                                onClick={() => setApprovalStatus(item.item_id, e.on_hold ? 'none' : 'on_hold')}
                                style={{
                                  borderColor: '#eab308',
                                  color: e.on_hold ? '#fff' : '#a16207',
                                  background: e.on_hold ? '#eab308' : 'transparent',
                                }}
                              >⏸</button>
                              <button
                                className="review-toggle-btn"
                                onClick={() => setApprovalStatus(item.item_id, e.rejected ? 'none' : 'rejected')}
                                style={{
                                  borderColor: '#ef4444',
                                  color: e.rejected ? '#fff' : '#ef4444',
                                  background: e.rejected ? '#ef4444' : 'transparent',
                                }}
                              >✕</button>
                            </div>
                            {e.rejected && e.rejected_reason && (
                              <div style={{ marginTop: 4, fontSize: 10, color: '#ef4444', fontStyle: 'italic', maxWidth: 120 }}>
                                "{e.rejected_reason}"
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            // ── READ-ONLY VIEW (anywhere outside the CC/Approval queue tabs) ──
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid black' }}>
              <h3 style={{ marginTop: 0, fontSize: 16 }}>Line Items</h3>
              <div style={{ overflowX: 'auto' }}>
                <table className="readonly-items-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={th({ position: 'sticky', left: 0, zIndex: 2, width: 240, minWidth: 240, maxWidth: 240 })}>Description</th>
                      <th style={th({ position: 'sticky', left: 240, zIndex: 2, width: 100, minWidth: 100, maxWidth: 100 })}>Unit</th>
                      <th style={th()}>Requested For</th>
                      <th style={th()}>Vendor</th>
                      <th style={th()}>Project</th>
                      <th style={th({ minWidth: 280, whiteSpace: 'normal' })}>BOQ Description</th>
                      <th style={th({ minWidth: 220, whiteSpace: 'normal' })}>BOQ Sub Component</th>
                      <th style={th()}>Priority</th>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a', minWidth: 110 })}>Qty</th>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a' })}>Est. Rate</th>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a' })}>Est. Amount</th>
                      <th style={th({ background: '#f0fdf4', color: '#0f172a' })}>VAT</th>
                      {hasCCData && (
                        <>
                          <th style={th({ background: '#eff6ff', color: '#0f172a' })}>CC Qty</th>
                          <th style={th({ background: '#eff6ff', color: '#0f172a' })}>CC Rate</th>
                          <th style={th({ background: '#eff6ff', color: '#0f172a' })}>CC Amt</th>
                          <th style={th({ background: '#eff6ff', color: '#0f172a' })}>CC Decision</th>
                        </>
                      )}
                      {hasApprovalData && (
                        <>
                          <th style={th({ background: '#fefce8', color: '#0f172a' })}>Appr. Qty</th>
                          <th style={th({ background: '#fefce8', color: '#0f172a' })}>Appr. Rate</th>
                          <th style={th({ background: '#fefce8', color: '#0f172a' })}>Appr. Amt</th>
                          <th style={th({ background: '#fefce8', color: '#0f172a' })}>Appr. Decision</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {prDetails.items?.map((item, idx) => {
                      const ccLabel = item.cost_control_approved ? 'CC APPROVED' : item.cost_control_rejected ? '✕ REJECTED' : '—';
                      const apprLabel = item.approved ? 'APPROVED' : item.on_hold ? 'ON HOLD' : item.rejected ? '✕ REJECTED' : '—';
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={td({ position: 'sticky', left: 0, zIndex: 1, background: '#fff', width: 240, minWidth: 240, maxWidth: 240 })}>{item.description}</td>
                          <td style={td({ position: 'sticky', left: 240, zIndex: 1, background: '#fff', width: 100, minWidth: 100, maxWidth: 100 })}>{item.unit_of_measure}</td>
                          <td style={td()}>{item.item_requested_for_display_name || '—'}</td>
                          <td style={td()}>{item.vendor_name || '—'}</td>
                          <td style={td()}>{item.project_name || '—'}</td>
                          <td style={td()}>{item.boq_item_description || '—'}</td>
                          <td style={td()}>{item.boq_component_description || '—'}</td>
                          <td style={td()}>{properCase(item.priority_status) || '—'}</td>
                          <td style={td({ background: '#f7fef7' })}>{fmtQty(item.quantity)}</td>
                          <td style={td({ background: '#f7fef7' })}>₦{fmt(item.estimated_unit_rate)}</td>
                          <td style={td({ background: '#f7fef7' })}>₦{fmt(item.estimated_amount)}</td>
                          <td style={td({ background: '#f7fef7' })}>{vatCell(item)}</td>
                          {hasCCData && (
                            <>
                              <td style={td({ background: '#f0f7ff' })}>{fmtQty(item.cost_control_quantity)}</td>
                              <td style={td({ background: '#f0f7ff' })}>₦{fmt(item.cost_control_unit_rate)}</td>
                              <td style={td({ background: '#f0f7ff' })}>₦{fmt(item.cost_control_amount)}</td>
                              <td style={td({ background: '#f0f7ff' })}>
                                <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: badgeBgFor(ccBadgeColor(item)), color: ccBadgeColor(item), border: `1px solid ${ccBadgeColor(item)}` }}>
                                  {ccLabel}
                                </span>
                              </td>
                            </>
                          )}
                          {hasApprovalData && (
                            <>
                              <td style={td({ background: '#fffdf0' })}>{fmtQty(item.approved_quantity)}</td>
                              <td style={td({ background: '#fffdf0' })}>₦{fmt(item.approved_unit_rate)}</td>
                              <td style={td({ background: '#fffdf0' })}>₦{fmt(item.approved_amount)}</td>
                              <td style={td({ background: '#fffdf0' })}>
                                <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: badgeBgFor(approvalBadgeColor(item)), color: approvalBadgeColor(item), border: `1px solid ${approvalBadgeColor(item)}` }}>
                                  {apprLabel}
                                </span>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        {/* Approval Progress & History — horizontal timeline, below the table */}
        <div style={{ background: '#fff', color: '#0f172a', padding: '12px 20px', borderRadius: 8, border: '1px solid black' }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1, color: '#64748b', marginBottom: 10 }}>
            <svg width="13" height="13" style={{ marginRight: 5, verticalAlign: 'middle' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            APPROVAL PROGRESS &amp; HISTORY
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto' }}>
            {/* Requester Step */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 150 }}>
              <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: '2px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
              </div>
              {trackingData.requester_details?.profile_pic && (
                <img src={trackingData.requester_details.profile_pic} alt="profile" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', marginTop: 6 }} />
              )}
              <div style={{ fontWeight: 600, fontSize: 13, marginTop: 6 }}>Submitted By</div>
              <div style={{ color: '#475569', fontSize: 12 }}>{trackingData.requester_details?.full_name}</div>
            </div>

            {/* Steps, ordered by STEP ORDER rather than "history then current".
                A request sent back by return_to_previous sits BEHIND the step
                that returned it, so simply concatenating the two lists drew the
                returned step to the LEFT of the pending one — backwards on the
                rail. Sorting on step_order puts them in workflow order. */}
            {timelineNodes.map((node) => {
              if (node.kind === 'history') {
                const hist = node.hist;
                const HIST_STYLE = {
                  approved: { color: '#10b981', icon: <polyline points="20 6 9 17 4 12" /> },
                  returned: { color: '#f97316', icon: (<><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>) },
                  rejected: { color: '#ef4444', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
                };
                const style = HIST_STYLE[hist.status] || HIST_STYLE.approved;
                const outcomeLabel = hist.status === 'returned' ? 'Returned' : hist.status === 'rejected' ? 'Rejected' : null;
                return (
                  <React.Fragment key={node.key}>
                    <div style={{ flex: '1 1 80px', minWidth: 80, height: 2, background: style.color, marginTop: 19 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 150, maxWidth: 200 }}>
                      <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: `1.5px solid ${style.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: `2px solid ${style.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: style.color }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">{style.icon}</svg>
                        </div>
                      </div>
                      {hist.approver_profile_pic && (
                        <img src={hist.approver_profile_pic} alt="profile" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', marginTop: 6 }} />
                      )}
                      <div style={{ fontWeight: 600, fontSize: 13, marginTop: 6 }}>
                        {hist.step_name}{outcomeLabel ? ` (${outcomeLabel})` : ''}
                      </div>
                      <div style={{ color: '#475569', fontSize: 12 }}>{hist.approver_fullname}</div>
                      <div style={{ color: '#64748b', fontSize: 10 }}>{hist.date_actioned ? new Date(hist.date_actioned).toLocaleString() : ''}</div>
                      {hist.comments && <div style={{ marginTop: 3, fontStyle: 'italic', color: '#475569', fontSize: 11 }}>"{hist.comments}"</div>}
                    </div>
                  </React.Fragment>
                );
              }

              const cur = trackingData.current_step;
              // Distinct from the component-level `onHold`: that is the
              // request's status, this is whether THIS timeline step carries
              // the hold flag. Named apart so the two are not confused.
              const stepOnHold = cur.is_on_hold;
              const stepColor = stepOnHold ? '#f97316' : '#eab308';
              return (
                <React.Fragment key={node.key}>
                  <div style={{ flex: '1 1 80px', minWidth: 80, height: 2, background: '#10b981', marginTop: 19 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 150, maxWidth: 200 }}>
                    <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: `1.5px solid ${stepColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: `2px solid ${stepColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: stepColor }}>
                        {stepOnHold ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                          </svg>
                        ) : (
                          <svg className="hourglass-pending" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 22h14" /><path d="M5 2h14" />
                            <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                            <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {cur.assigned_to?.profile_pic && (
                      <img src={cur.assigned_to.profile_pic} alt="profile" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', marginTop: 6 }} />
                    )}
                    <div style={{ fontWeight: 600, fontSize: 13, color: stepColor, marginTop: 6 }}>
                      <div>{cur.step_name}</div>
                      <div>{stepOnHold ? '(On Hold)' : '(Pending)'}</div>
                    </div>
                    {cur.assigned_to?.full_name && (
                      <div style={{ color: '#475569', fontSize: 12 }}>{cur.assigned_to.full_name}</div>
                    )}
                    {stepOnHold && cur.hold_comment && (
                      <div style={{ marginTop: 3, fontStyle: 'italic', color: '#9a3412', fontSize: 11 }}>"{cur.hold_comment}"</div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {/* Next Step — a preview of where this goes once the current pending step clears */}
            {trackingData.next_step && (
              <>
                <div style={{ flex: '1 1 80px', minWidth: 80, height: 2, background: '#eab308', borderTop: '2px dashed #eab308', marginTop: 19 }} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 150, maxWidth: 220, opacity: 0.75 }}>
                  <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: '1.5px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: '2px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#64748b', marginTop: 6 }}>{trackingData.next_step.step_name} (Next)</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Rejection Reason Modal ── */}
      {rejectReasonModal && (
        <Modal title={rejectReasonModal.label} onClose={() => setRejectReasonModal(null)}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
              {rejectReasonModal.field === 'cost_control_rejected_reason' || rejectReasonModal.label?.includes('Rejection')
                ? 'Reason for Rejection'
                : 'Reason for putting this line on hold'} <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              value={rejectReasonModal.currentReason}
              onChange={(v) => setRejectReasonModal((prev) => ({ ...prev, currentReason: v }))}
              placeholder="Enter reason..."
              as="textarea"
              rows={3}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" onClick={() => setRejectReasonModal(null)}>Cancel</Btn>
            <Btn
              style={{ background: rejectReasonModal.label?.includes('Hold') ? '#eab308' : '#ef4444', color: '#fff', border: 'none' }}
              onClick={() => {
                updateLineEdit(rejectReasonModal.itemId, rejectReasonModal.field, rejectReasonModal.currentReason);
                setRejectReasonModal(null);
              }}
            >Save Reason</Btn>
          </div>
        </Modal>
      )}

      {/* ── Action Modal ── */}
      {showActionModal && (() => {
        const ACTION_META = {
          approve: { label: 'Approve', title: 'Approve Request', color: '#16a34a', verb: 'approve', commentRequired: false, placeholder: 'Optional comment...' },
          return: { label: 'Return', title: 'Return Request', color: '#f97316', verb: 'return', commentRequired: true, placeholder: 'Explain why this request is being returned...' },
          reject: { label: 'Reject', title: 'Reject Request', color: '#ef4444', verb: 'reject', commentRequired: true, placeholder: 'Explain why this request is being rejected...' },
          hold: { label: 'Hold', title: 'Put Request On Hold', color: '#eab308', verb: 'put on hold', commentRequired: true, placeholder: 'Explain why this request is being put on hold...' },
          resume: { label: 'Resume', title: 'Lift Hold', color: '#16a34a', verb: 'take off hold', commentRequired: false, placeholder: 'Optional note...' },
        };
        const meta = ACTION_META[actionType] || ACTION_META.approve;
        return (
        <Modal title={meta.title} onClose={() => setShowActionModal(false)}>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              You are about to <strong>{meta.verb}</strong> the purchase request.
              {actionType === 'reject' && ' This is final — the request will not be resubmittable.'}
              {actionType === 'hold' && ' The request stays with you and you can act on it again later.'}
            </p>
            {!actionReviewed && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                <span>⚠️</span>
                <span>
                  {hasAnyReview() ? 'Some line items have not been reviewed.' : 'You did not review the line items.'}{' '}
                  {actionType === 'approve' && 'Unreviewed line items will automatically be marked ACCEPTED.'}
                  {actionType === 'reject' && "Unreviewed line items will automatically be marked REJECTED, using your comment below as each line's rejection reason."}
                  {actionType === 'hold' && "Unreviewed line items will automatically be marked ON HOLD, using your comment below as each line's hold reason."}
                  {actionType === 'return' && 'You can still return this request without reviewing them.'}
                </span>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                Comment {meta.commentRequired && <span style={{ color: 'red' }}>*</span>}
              </label>
              <Input value={actionComment} onChange={setActionComment} placeholder={meta.placeholder} as="textarea" rows={3} />
            </div>

            {/* Where the return lands. Defaults to whatever the step is
                configured to do, so accepting it reproduces the previous
                behaviour; the approver can override to any step this request
                has already passed through, or all the way to the requester.
                The options come from the server, which validates against the
                same rule, so nothing offered here can be refused. */}
            {actionType === 'return' && returnTargets.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Return to <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  title="Return To"
                  value={returnTarget}
                  onChange={setReturnTarget}
                  options={returnTargets.map((t) => ({
                    value: t.value,
                    label: t.is_default ? `${t.label} (default)` : t.label,
                  }))}
                />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                  {returnTargets.find((t) => t.value === returnTarget)?.description
                    || 'Choose where this request goes back to.'}
                </div>
              </div>
            )}

            {/* Why the request is being parked. Optional — a return is valid
                without one — but it is what groups the Cost Control queue's
                Pending Consideration panels, so it is worth prompting for. */}
            {actionType === 'return' && considerationStatuses.length > 0
              && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget) && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Consideration Status <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  title="Consideration Status"
                  value={considerationStatusId}
                  onChange={setConsiderationStatusId}
                  placeholder="Choose a heading"
                  options={considerationOptionsFor(considerationStatuses, returnFromStep).map((c) => ({
                    value: c.id,
                    label: `${c.icon ? c.icon + ' ' : ''}${c.name}`,
                  }))}
                />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Groups the request under this heading in the Cost Control queue.
                </div>
              </div>
            )}
            {actionType === 'approve' && nextStepInfo && nextStepInfo.approver_mode !== 'is_final' && nextStepInfo.step_name && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  {nextStepInfo.approver_mode === 'returning_step'
                    ? `Goes back to - ${nextStepInfo.step_name}`
                    : `Next Approval Step - ${nextStepInfo.step_name}`}
                </label>
                {nextStepInfo.approver_mode === 'returning_step' ? (
                  // The returning approver is already recorded on the step, so
                  // there is nobody to nominate — just say where it is going.
                  <ReturnDestinationNote stepName={nextStepInfo.step_name} />
                ) : nextStepInfo.approver_mode === 'specific_user' ? (
                  <Select
                    value={nextApproverId}
                    onChange={setNextApproverId}
                    options={nextStepInfo.eligible_users.map((a) => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
                  />
                ) : (
                  <Select
                    value="step_queue"
                    onChange={() => {}}
                    options={[{ value: 'step_queue', label: `🏢 ${nextStepInfo.step_name}` }]}
                  />
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" onClick={() => setShowActionModal(false)} disabled={submittingAction}>Cancel</Btn>
            <Btn
              onClick={submitAction}
              disabled={submittingAction || (
                actionType === 'return'
                && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)
                && !considerationStatusId
              )}
              style={{ background: meta.color, color: '#fff', border: 'none' }}
            >
              {submittingAction ? 'Submitting...' : `Confirm ${meta.label}`}
            </Btn>
          </div>
        </Modal>
        );
      })()}

      {/* ── Outcome Modal ── */}
      {outcomeModal && (() => {
        const OUTCOME_STYLE = {
          approve: { color: '#16a34a', bg: '#dcfce7', shadow: 'rgba(22,163,74,0.25)', heading: 'REQUEST APPROVED!', icon: <polyline points="20 6 9 17 4 12" /> },
          return: { color: '#f97316', bg: '#ffedd5', shadow: 'rgba(249,115,22,0.25)', heading: 'REQUEST RETURNED!', icon: (<><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>) },
          reject: { color: '#ef4444', bg: '#fee2e2', shadow: 'rgba(239,68,68,0.25)', heading: 'REQUEST REJECTED!', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
          hold: { color: '#eab308', bg: '#fef9c3', shadow: 'rgba(234,179,8,0.25)', heading: 'REQUEST ON HOLD!', icon: (<><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></>) },
          // Needs its own entry: the fallback here is the ERROR style, so a
          // successful resume would have rendered as a red "ACTION FAILED".
          resume: { color: '#16a34a', bg: '#dcfce7', shadow: 'rgba(22,163,74,0.25)', heading: 'HOLD LIFTED!', icon: <polygon points="5 3 19 12 5 21 5 3" /> },
          error: { color: '#ef4444', bg: '#fee2e2', shadow: 'rgba(239,68,68,0.25)', heading: 'ACTION FAILED', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
        };
        const style = OUTCOME_STYLE[outcomeModal.type] || OUTCOME_STYLE.error;
        const wasSuccess = outcomeModal.type !== 'error';
        return (
        <Modal
          title={outcomeModal.title}
          onClose={() => {
            setOutcomeModal(null);
            if (wasSuccess && onBack) onBack();
          }}
        >
          <div style={{ textAlign: 'center', padding: '20px 10px' }}>
            <div
              style={{
                width: 72, height: 72, borderRadius: '50%',
                background: style.bg,
                border: `3px solid ${style.color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px auto',
                boxShadow: `0 0 24px ${style.shadow}`,
              }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={style.color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                {style.icon}
              </svg>
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: style.color, margin: '0 0 10px 0', letterSpacing: '-0.02em' }}>
              {style.heading}
            </h3>
            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: '0 0 24px 0', maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
              {outcomeModal.message}
            </p>
            <button
              onClick={() => {
                setOutcomeModal(null);
                if (wasSuccess && onBack) onBack();
              }}
              style={{
                background: style.color,
                color: '#ffffff', border: 'none', padding: '12px 36px',
                borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              {wasSuccess ? 'OK / Continue' : 'Close'}
            </button>
          </div>
        </Modal>
        );
      })()}
    </div>
  );
}
